// Native JavaScript date/time utilities to replace Moment.js dependency (CORRECTED VERSION)
// Maintains full backward compatibility with existing general_utilities.js functions
define(function (require) {
	const CN = require('civicsUtils/constants');

	// Cache the user's locale once during initialization for performance
	// This avoids repeatedly calling the user language function
	let _userLocale = 'en';
	
	// Cache for detected locale format patterns (e.g., MM/DD/YYYY vs DD/MM/YYYY)
	// This is expensive to calculate, so we cache it after first detection
	// Contains regex patterns and index mappings for date/time component extraction
	let _localeFormats = null;

	// Reference to the user language resolver supplied by general_utilities.init().
	// Retained so the locale can be re-resolved lazily on each call rather than being
	// frozen at module-load time (see _ensureLocale below).
	let _userLangFunction = null;

	return {
		// Initialize with user language function from general_utilities
		init: function (userLangFunction) {
			// Retain the resolver so we can re-check the locale later. The site culture
			// (GLOBAL_CONFIG.cultureInfo) may not be in sessionStorage yet when this runs
			// on a freshly opened tab, so a value cached here can be wrong (e.g. defaults
			// to navigator.language). _ensureLocale() corrects it once config is available.
			_userLangFunction = userLangFunction || null;
			// Seed an initial value for immediate use.
			_userLocale = userLangFunction ? userLangFunction() : 'en';
			// Detect format patterns for the locale
			_localeFormats = this._detectLocaleFormats(_userLocale);
		},

		// Re-resolves the user locale on demand and recomputes the cached format patterns
		// only when the locale has actually changed. This avoids the "frozen at load time"
		// bug where a tab opened before the site culture is available parses dates using
		// the wrong day/month order (MM/DD vs DD/MM), while keeping the common case cheap.
		_ensureLocale: function () {
			if (!_userLangFunction) {
				return;
			}
			const currentLocale = _userLangFunction() || 'en';
			if (currentLocale !== _userLocale || !_localeFormats) {
				_userLocale = currentLocale;
				_localeFormats = this._detectLocaleFormats(_userLocale);
			}
		},

		// Parses date strings with multiple format fallbacks (ISO, localized datetime, date-only, time-only)
		// Equivalent to Moment.js parsing with strict/lenient modes
		// 
		// PARSING STRATEGY: This function tries multiple approaches in order of specificity:
		// 1. ISO format (2023-01-14T15:45:30) - most reliable, international standard
		// 2. Locale datetime (01/14/2023 3:45 PM or 14/01/2023 15:45) - depends on user's locale
		// 3. Date-only (01/14/2023 or 14/01/2023) - sets time to midnight
		// 4. Time-only (3:45 PM or 15:45) - uses today's date
		//
		// NOTE: We should probably stop using this function in our code and instead call the appropriate date function instead
		// For example, if we want a date from a string date, call dateString.
		parseDateString: function (dateString) {
			// Input validation: reject null, undefined, empty strings, or non-strings
			if (!dateString || typeof dateString !== 'string' || !dateString.trim()) {
				return null;
			}

			// Make sure the locale/format patterns reflect the current site culture,
			// which may have become available after this module was first loaded.
			this._ensureLocale();

			// Remove timezone info first (e.g., "2023-01-14T15:45:30-05:00" becomes "2023-01-14T15:45:30")
			// This simplifies parsing by standardizing the input format
			const cleanDateString = this.removeTimeZone(dateString.trim());

			// STEP 1: Check for ISO format (has 'T' separator between date and time)
			// ISO format is most reliable: YYYY-MM-DDTHH:mm:ss
			if (cleanDateString.indexOf('T') >= 0) {
				const date = this._parseISOFormat(cleanDateString);
				if (date) return date;
			}

			// STEP 2: Try locale-aware datetime parsing (strict mode)
			// Handles formats like "01/14/2023 3:45:30 PM" (US) or "14/01/2023 15:45:30" (NZ/AU)
			let date = this._parseLocaleDateTime(cleanDateString, true);
			if (date) return date;

			// STEP 3: Try locale-aware date-only parsing (strict mode)  
			// Handles formats like "01/14/2023" (US) or "14/01/2023" (NZ/AU)
			// Time will be set to midnight (00:00:00)
			date = this._parseLocaleDate(cleanDateString, true);
			if (date) return date;

			// STEP 4: Try time-only parsing (lenient fallback)
			// Handles formats like "3:45 PM" or "15:45"
			// Uses today's date with the parsed time
			return this._parseLocaleTime(cleanDateString, false);
		},

		// Formats date using locale-specific format (equivalent to Moment's 'L' format)
		// 
		// INPUT: Date string in various formats (ISO, locale-specific, etc.)
		// OUTPUT: Locale-formatted date string (e.g., "01/14/2023" for US, "14/01/2023" for NZ)
		// 
		// Examples:
		//   dateString("2023-01-14T15:45:30") → "01/14/2023" (US locale)
		//   dateString("2023-01-14T15:45:30") → "14/01/2023" (NZ locale)
		//   dateString("01/14/2023") → "01/14/2023" (US locale)
		//   dateString("") → ""
		//   dateString(null) → ""
		dateString: function (dateString) {
			if (dateString == null || (typeof dateString === 'string' && dateString.trim() === '')) {
				return '';
			}

			// parseDateString() calls _ensureLocale() before returning, so _userLocale is
			// already refreshed by the time the formatter below reads it.
			const date = this.parseDateString(dateString);
			if (!date || isNaN(date.getTime())) {
				return '';
			}

			const locale = _userLocale;
			try {
				// Use Intl.DateTimeFormat for proper locale-aware formatting
				// This replaces Moment.js dependency with native browser API
				// Format options: year=numeric (2023), month/day=2-digit (01, 14)
				const formatter = new Intl.DateTimeFormat(locale, {
					year: 'numeric',    // 4-digit year (2023)
					month: '2-digit',   // Zero-padded month (01-12)
					day: '2-digit'      // Zero-padded day (01-31)
				});
				return formatter.format(date);
			} catch (error) {
				// Fallback to English format if locale is invalid or unsupported
				// This ensures the function never crashes due to locale issues
				return new Intl.DateTimeFormat('en', {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit'
				}).format(date);
			}
		},

		// Converts Date objects to locale-formatted strings
		dateStringFromDate: function (date) {
			if (date != null && date != undefined && date instanceof Date && !isNaN(date.getTime())) {
				this._ensureLocale();
				const locale = _userLocale;
				try {
					const formatter = new Intl.DateTimeFormat(locale, {
						year: 'numeric',
						month: '2-digit',
						day: '2-digit'
					});
					return formatter.format(date);
				} catch (error) {
					// Fallback to English format
					return new Intl.DateTimeFormat('en', {
						year: 'numeric',
						month: '2-digit',
						day: '2-digit'
					}).format(date);
				}
			}
			return '';
		},

		// Handles datetime parsing/formatting with ISO output
		buildISOStringFromDateAndTime: function (dateString, timeString) {
			if (dateString == null || (typeof dateString === 'string' && dateString.trim() === '')) {
				return CN.DATE_TIME_MIN_VAL;
			}

			// parseDateString() refreshes the locale; the ISO output below is locale-independent.
			let date = this.parseDateString(dateString);
			if (!date || isNaN(date.getTime())) {
				return '';
			}

			// If timeString is provided, parse and apply it
			if (timeString && timeString.length > 0) {
				const timeDate = this._parseTimeString(this.removeTimeZone(timeString));
				if (timeDate) {
					date.setHours(timeDate.getHours());
					date.setMinutes(timeDate.getMinutes());
					date.setSeconds(timeDate.getSeconds());
				}
			}

			// Format as ISO datetime: YYYY-MM-DD[T]HH:mm:ss
			return this._formatAsISO(date);
		},

		// Converts Date to ISO datetime format
		buildISOStringFromDate: function (date) {
			if (date != null && date != undefined && date instanceof Date && !isNaN(date.getTime())) {
				return this._formatAsISO(date);
			}
			return '';
		},

		// Formats time using locale-specific format (equivalent to Moment's 'LT' format)
		// 
		// INPUT: Date string or datetime string
		// OUTPUT: Locale-formatted time string WITHOUT SECONDS (matches Moment.js 'LT' format)
		// 
		// Examples:
		//   timeString("2023-01-14T15:45:30") → "3:45 PM" (US locale)
		//   timeString("2023-01-14T15:45:30") → "3:45 pm" (NZ locale)
		//   timeString("15:45") → "3:45 PM" (US locale, parsed as time-only)
		timeString: function (dateString) {
			if (dateString == null || (typeof dateString === 'string' && dateString.trim() === '')) {
				return '';
			}

			// parseDateString() calls _ensureLocale() before returning, so _userLocale is
			// already refreshed by the time the formatter below reads it.
			const date = this.parseDateString(dateString);
			if (!date || isNaN(date.getTime())) {
				return '';
			}

			const locale = _userLocale;
			try {
				// Match Moment.js 'LT' format - NO SECONDS
				const formatter = new Intl.DateTimeFormat(locale, {
					hour: 'numeric',
					minute: '2-digit'
				});
				return formatter.format(date);
			} catch (error) {
				// Fallback to English format
				return new Intl.DateTimeFormat('en', {
					hour: 'numeric',
					minute: '2-digit'
				}).format(date);
			}
		},

		civicsDateTimeString: function (date, time, removeOffset) {
			if ((typeof date === 'string' && date.trim() === '') && !(date instanceof Date)) {
				return '';
			}

			let dt = null;
			// If the passed parameter date is already of type Date then we won't need to parse
			if (date instanceof Date) {
				dt = new Date(date); // Create a copy to avoid modifying original
			} else {
				const combinedString = time ? date + " " + time : date;
				dt = this.parseDateString(combinedString);
				if (!dt) {
					return '';
				}
			}

			// Remove timezone offset if requested (convert to UTC-like representation)
			if (removeOffset) {
				// getTimezoneOffset() returns minutes difference from UTC (positive for behind UTC)
				// Multiply by 60000 to convert minutes to milliseconds
				// Subtract to counter the offset (if we're UTC-6, add 6 hours worth of milliseconds)
				const tzOffset = dt.getTimezoneOffset() * 60000;
				dt = new Date(dt.getTime() - tzOffset);
			}

			if (isNaN(dt.getTime())) {
				return '';
			}

			let val = dt.toISOString();
			if (val !== '' && val.endsWith('Z')) {
				// Remove Z in ISO format
				val = val.substring(0, val.length - 1);
			}
			return val;
		},

		// Removes timezone offset from ISO datetime strings to simplify parsing
		// 
		// PURPOSE: Strips timezone info to standardize date strings before parsing
		// This prevents timezone conversion issues and focuses on the local date/time values
		// 
		// ALGORITHM:
		// 1. Look for ISO format with 'T' separator (YYYY-MM-DDTHH:MM:SS±HH:00)
		// 2. Split on 'T' to separate date and time parts
		// 3. Remove timezone offset (±HH:00) from time part
		// 4. Reassemble without timezone info
		// 
		// Examples:
		//   "2023-01-14T15:45:30-05:00" → "2023-01-14T15:45:30"
		//   "2023-01-14T15:45:30+03:00" → "2023-01-14T15:45:30"
		//   "2023-01-14T15:45:30.000Z" → "2023-01-14T15:45:30.000Z" (unchanged - no offset)
		//   "01/14/2023" → "01/14/2023" (unchanged - not ISO format)
		removeTimeZone: function (dateString) {
			if (!dateString || typeof dateString !== 'string') {
				return dateString;
			}

			// Only process strings that look like ISO datetime format (contain 'T' separator)
			// Expected format: YYYY-MM-DDTHH:MM:SS±HH:00 where ±HH:00 is the timezone offset
			const dateTimeSplit = dateString.split('T');
			if (dateTimeSplit.length == 2) {
				// Split the time component on negative offset (-)
				let timeSplit = dateTimeSplit[1].split('-');

				if (timeSplit.length != 2) {
					// If there wasn't a negative offset, check for positive offset (+)
					timeSplit = dateTimeSplit[1].split('+');
				}

				if (timeSplit.length == 2) {
					// Reassemble: date part + 'T' + time part (without timezone offset)
					// Example: "2023-01-14" + "T" + "15:45:30" = "2023-01-14T15:45:30"
					return dateTimeSplit[0] + 'T' + timeSplit[0];
				}
			}
			// Return unchanged if not in expected format or no timezone offset found
			return dateString;
		},

		// Private helper methods
		_parseISOFormat: function (dateString) {
			// Handle ISO format: "YYYY-MM-DD[T]HH:mm:ss"
			const isoPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/;
			if (isoPattern.test(dateString)) {
				const date = new Date(dateString);
				return isNaN(date.getTime()) ? null : date;
			}
			return null;
		},

		_parseLocaleDateTime: function (dateString, strict) {
			// Use detected format patterns instead of assumptions
			if (!_localeFormats) {
				_localeFormats = this._detectLocaleFormats(_userLocale);
			}
			
			const pattern = _localeFormats.dateTime;
			const match = dateString.match(pattern.pattern);
			
			if (match) {
				const year = parseInt(match[pattern.yearIndex]);
				const month = parseInt(match[pattern.monthIndex]) - 1; // JavaScript Date months are 0-based: Jan=0, Dec=11
				const day = parseInt(match[pattern.dayIndex]);
				let hour = parseInt(match[pattern.hourIndex]);
				const minute = parseInt(match[pattern.minuteIndex]);
				const second = parseInt(match[pattern.secondIndex] || 0);
				const ampm = match[pattern.ampmIndex];
				
				// Handle AM/PM conversion to 24-hour format
				// CONVERSION RULES:
				// - PM hours 1-11: add 12 (1 PM = 13, 11 PM = 23)
				// - PM hour 12: keep as 12 (12 PM = 12 noon)
				// - AM hours 1-11: keep as-is (1 AM = 1, 11 AM = 11)  
				// - AM hour 12: convert to 0 (12 AM = 0 midnight)
				if (ampm) {
					if (ampm.toUpperCase() === 'PM' && hour !== 12) {
						hour += 12;  // 1 PM → 13, 2 PM → 14, etc.
					} else if (ampm.toUpperCase() === 'AM' && hour === 12) {
						hour = 0;    // 12 AM → 0 (midnight)
					}
					// Note: 12 PM stays 12, AM hours 1-11 stay unchanged
				}
				
				const date = new Date(year, month, day, hour, minute, second);
				return isNaN(date.getTime()) ? null : date;
			}
			
			// Fallback to legacy parsing if pattern doesn't match
			return this._legacyParseDateTime(dateString, strict);
		},

		_parseLocaleDate: function (dateString, strict) {
			// Use detected format patterns instead of assumptions
			if (!_localeFormats) {
				_localeFormats = this._detectLocaleFormats(_userLocale);
			}
			
			const pattern = _localeFormats.date;
			const match = dateString.match(pattern.pattern);
			
			if (match) {
				const year = parseInt(match[pattern.yearIndex]);
				const month = parseInt(match[pattern.monthIndex]) - 1; // JavaScript Date months are 0-based: Jan=0, Dec=11
				const day = parseInt(match[pattern.dayIndex]);
				
				const date = new Date(year, month, day);
				return isNaN(date.getTime()) ? null : date;
			}
			
			// Try MM/DD format (no year) or DD/MM format (no year)
			const shortPatterns = [
				/^(\d{1,2})\/(\d{1,2})$/,
			];
			
			for (const shortPattern of shortPatterns) {
				const shortMatch = dateString.match(shortPattern);
				if (shortMatch) {
					const currentYear = new Date().getFullYear();
					
					// AMBIGUOUS DATE HANDLING: When we have MM/DD format without year,
					// we don't know if "01/14" means Jan 14th or 1st of the 14th month (invalid).
					// Try both interpretations based on detected locale pattern preference
					const interpretations = [
						{ month: parseInt(shortMatch[pattern.monthIndex === 1 ? 1 : 2]) - 1, day: parseInt(shortMatch[pattern.dayIndex === 1 ? 1 : 2]) },
						{ month: parseInt(shortMatch[pattern.monthIndex === 2 ? 2 : 1]) - 1, day: parseInt(shortMatch[pattern.dayIndex === 2 ? 2 : 1]) }
					];
					
					for (const interp of interpretations) {
						if (interp.month >= 0 && interp.month <= 11 && interp.day >= 1 && interp.day <= 31) {
							const date = new Date(currentYear, interp.month, interp.day);
							if (!isNaN(date.getTime())) {
								return date;
							}
						}
					}
				}
			}
			
			// Fallback to legacy parsing if pattern doesn't match
			return this._legacyParseDate(dateString, strict);
		},

		_parseLocaleTime: function (dateString, strict) {
			// Use detected format patterns instead of assumptions
			if (!_localeFormats) {
				_localeFormats = this._detectLocaleFormats(_userLocale);
			}
			
			const pattern = _localeFormats.time;
			const match = dateString.match(pattern.pattern);
			
			if (match) {
				let hour = parseInt(match[pattern.hourIndex]);
				const minute = parseInt(match[pattern.minuteIndex]);
				const second = parseInt(match[pattern.secondIndex] || 0);
				const ampm = match[pattern.ampmIndex];
				
				// Handle AM/PM conversion
				if (ampm) {
					if (ampm.toUpperCase() === 'PM' && hour !== 12) {
						hour += 12;
					} else if (ampm.toUpperCase() === 'AM' && hour === 12) {
						hour = 0;
					}
				}
				
				// Create today's date with parsed time
				const today = new Date();
				today.setHours(hour, minute, second, 0);
				return today;
			}
			
			// Fallback to legacy parsing
			return this._legacyParseTime(dateString, strict);
		},

		// Enhanced parsing functions for specific formats
		_tryParseMMDDYYYYDateTime: function (dateString, strict) {
			// MM/DD/YYYY HH:mm:ss AM/PM format
			const pattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i;
			const match = dateString.match(pattern);

			if (match) {
				const month = parseInt(match[1]) - 1; // 0-based month
				const day = parseInt(match[2]);
				const year = parseInt(match[3]);
				let hour = parseInt(match[4]);
				const minute = parseInt(match[5]);
				const second = parseInt(match[6] || 0);
				const ampm = match[7];

				// Handle AM/PM
				if (ampm) {
					if (ampm.toUpperCase() === 'PM' && hour !== 12) {
						hour += 12;
					} else if (ampm.toUpperCase() === 'AM' && hour === 12) {
						hour = 0;
					}
				}

				const date = new Date(year, month, day, hour, minute, second);
				return isNaN(date.getTime()) ? null : date;
			}
			return null;
		},

		_tryParseDDMMYYYYDateTime: function (dateString, strict) {
			// DD/MM/YYYY HH:mm:ss format (24-hour assumed for NZ/AU)
			const pattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
			const match = dateString.match(pattern);

			if (match) {
				const day = parseInt(match[1]);
				const month = parseInt(match[2]) - 1; // 0-based month
				const year = parseInt(match[3]);
				const hour = parseInt(match[4]);
				const minute = parseInt(match[5]);
				const second = parseInt(match[6] || 0);

				const date = new Date(year, month, day, hour, minute, second);
				return isNaN(date.getTime()) ? null : date;
			}
			return null;
		},

		_tryParseMMDDYYYY: function (dateString, strict) {
			// MM/DD/YYYY format
			const pattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
			const match = dateString.match(pattern);

			if (match) {
				const month = parseInt(match[1]) - 1; // 0-based month
				const day = parseInt(match[2]);
				const year = parseInt(match[3]);

				const date = new Date(year, month, day);
				return isNaN(date.getTime()) ? null : date;
			}

			// Also try MM/DD format (no year)
			const shortPattern = /^(\d{1,2})\/(\d{1,2})$/;
			const shortMatch = dateString.match(shortPattern);
			if (shortMatch) {
				const month = parseInt(shortMatch[1]) - 1;
				const day = parseInt(shortMatch[2]);
				const currentYear = new Date().getFullYear();

				const date = new Date(currentYear, month, day);
				return isNaN(date.getTime()) ? null : date;
			}

			return null;
		},

		_tryParseDDMMYYYY: function (dateString, strict) {
			// DD/MM/YYYY format
			const pattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
			const match = dateString.match(pattern);

			if (match) {
				const day = parseInt(match[1]);
				const month = parseInt(match[2]) - 1; // 0-based month
				const year = parseInt(match[3]);

				// Validate reasonable ranges
				if (day > 31 || month > 11) {
					return null;
				}

				const date = new Date(year, month, day);
				return isNaN(date.getTime()) ? null : date;
			}

			// Also try DD/MM format (no year)
			const shortPattern = /^(\d{1,2})\/(\d{1,2})$/;
			const shortMatch = dateString.match(shortPattern);
			if (shortMatch) {
				const day = parseInt(shortMatch[1]);
				const month = parseInt(shortMatch[2]) - 1;

				if (day > 31 || month > 11) {
					return null;
				}

				const currentYear = new Date().getFullYear();
				const date = new Date(currentYear, month, day);
				return isNaN(date.getTime()) ? null : date;
			}

			return null;
		},

		_parseTimeString: function (timeString) {
			if (!timeString || typeof timeString !== 'string') {
				return null;
			}

			// Enhanced time parsing with multiple formats
			const patterns = [
				/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i, // 12-hour with AM/PM
				/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/ // 24-hour format
			];

			for (const pattern of patterns) {
				const match = timeString.match(pattern);
				if (match) {
					let hour = parseInt(match[1]);
					const minute = parseInt(match[2]);
					const second = parseInt(match[3] || 0);
					const ampm = match[4];

					// Handle AM/PM
					if (ampm) {
						if (ampm.toUpperCase() === 'PM' && hour !== 12) {
							hour += 12;
						} else if (ampm.toUpperCase() === 'AM' && hour === 12) {
							hour = 0;
						}
					}

					const today = new Date();
					today.setHours(hour, minute, second, 0);
					return today;
				}
			}
			return null;
		},

		_formatAsISO: function (date) {
			const year = String(date.getFullYear()).padStart(4,'0');//need to ensure we have a 4 digit year, which we don't with the "null" date 1/1/1T1:1:1
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			const hours = String(date.getHours()).padStart(2, '0');
			const minutes = String(date.getMinutes()).padStart(2, '0');
			const seconds = String(date.getSeconds()).padStart(2, '0');

			return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
		},

		// Detect locale-specific date/time format patterns using test date with unique components
		// 
		// CLEVER STRATEGY: We use a test date with unique, easily identifiable components
		// to reverse-engineer how the user's locale formats dates and times.
		// 
		// WHY THIS WORKS:
		// 1. Format a known date using the user's locale
		// 2. Look for our unique components in the formatted output
		// 3. Determine component order and build regex patterns for parsing
		// 
		// TEST DATE COMPONENTS (chosen to be unique and unambiguous):
		// - Year: 2023 (4 digits, easy to find)
		// - Month: 11 (November, formatted as "11" - not confused with day)  
		// - Day: 22 (2 digits, different from month)
		// - Hour: 13 (1 PM, clearly identifies 24hr vs 12hr format)
		// - Minute: 14 (unique from other components)
		// - Second: 15 (unique from other components)
		_detectLocaleFormats: function(locale) {
			try {
				// Create test date with unique components for pattern detection
				// Year: 2023, Month: 11 (Nov), Day: 22, Hour: 13, Minute: 14, Second: 15
				const testDate = new Date(2023, 10, 22, 13, 14, 15); // Note: JavaScript months are 0-based (10 = November)
				
				// Format the test date using the locale's format
				const dateFormatter = new Intl.DateTimeFormat(locale, {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit'
				});
				
				const timeFormatter = new Intl.DateTimeFormat(locale, {
					hour: 'numeric',
					minute: '2-digit',
					second: '2-digit'
				});
				
				const dateTimeFormatter = new Intl.DateTimeFormat(locale, {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour: 'numeric',
					minute: '2-digit',
					second: '2-digit'
				});
				
				// Get formatted strings
				const formattedDate = dateFormatter.format(testDate);
				const formattedTime = timeFormatter.format(testDate);
				const formattedDateTime = dateTimeFormatter.format(testDate);
				
				// Parse the formatted strings to detect component positions and build regex patterns
				// Example: if formattedDate = "11/22/2023", we know month comes first, then day, then year
				const datePattern = this._buildDatePattern(formattedDate, '2023', '11', '22');
				const timePattern = this._buildTimePattern(formattedTime, '13', '14', '15');
				const dateTimePattern = this._buildDateTimePattern(formattedDateTime, '2023', '11', '22', '13', '14', '15');
				
				return {
					date: datePattern,
					time: timePattern,
					dateTime: dateTimePattern,
					formattedSamples: {
						date: formattedDate,
						time: formattedTime,
						dateTime: formattedDateTime
					}
				};
			} catch (error) {
				// Fallback to US format if detection fails
				return {
					date: { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, monthIndex: 1, dayIndex: 2, yearIndex: 3 },
					time: { pattern: /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i, hourIndex: 1, minuteIndex: 2, secondIndex: 3, ampmIndex: 4 },
					dateTime: { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i, monthIndex: 1, dayIndex: 2, yearIndex: 3, hourIndex: 4, minuteIndex: 5, secondIndex: 6, ampmIndex: 7 }
				};
			}
		},
		
		// Build date parsing pattern by analyzing component positions
		// 
		// REVERSE ENGINEERING APPROACH:
		// 1. We know what the formatted output looks like (e.g., "11/22/2023")
		// 2. We know what our test values are ("2023", "11", "22")  
		// 3. We find where each component appears in the formatted string
		// 4. We build a regex that can extract these components from similar strings
		// 
		// Example: formattedDate="11/22/2023", year="2023", month="11", day="22"
		// Result: { pattern: /^(\d{1,4})\/(\d{1,4})\/(\d{1,4})$/, monthIndex: 1, dayIndex: 2, yearIndex: 3 }
		_buildDatePattern: function(formattedDate, year, month, day) {
			// Find the position of each component in the formatted string
			// This tells us the order: MM/DD/YYYY vs DD/MM/YYYY vs YYYY/MM/DD
			const yearPos = formattedDate.indexOf(year);   // Find "2023"
			const monthPos = formattedDate.indexOf(month); // Find "11"  
			const dayPos = formattedDate.indexOf(day);     // Find "22"
			
			// Create objects to track component order and sort by position
			const components = [
				{ type: 'year', pos: yearPos, value: year },
				{ type: 'month', pos: monthPos, value: month },
				{ type: 'day', pos: dayPos, value: day }
			].filter(c => c.pos >= 0).sort((a, b) => a.pos - b.pos); // Sort by appearance order
			
			// Build regex pattern dynamically based on detected component order
			let pattern = '^';       // Start of string
			let indexMap = {};       // Maps component type to regex group number
			let groupIndex = 1;      // Regex group counter (starts at 1)
			
			for (let i = 0; i < components.length; i++) {
				if (i > 0) {
					// Add separator pattern between components (/, -, or .)
					pattern += '[\/\\-\\.]';
				}
				// Add capture group for digits (1-4 digits to handle various formats)
				pattern += '(\\d{1,4})';
				// Remember which regex group corresponds to this component
				indexMap[components[i].type + 'Index'] = groupIndex++;
			}
			pattern += '$';          // End of string
			
			// Return pattern with index mapping
			// Example result: { pattern: /^(\d{1,4})\/(\d{1,4})\/(\d{1,4})$/, monthIndex: 1, dayIndex: 2, yearIndex: 3 }
			return Object.assign({ pattern: new RegExp(pattern) }, indexMap);
		},
		
		// Build time parsing pattern by analyzing component positions
		_buildTimePattern: function(formattedTime, hour, minute, second) {
			// Check for AM/PM indicator to determine 12-hour vs 24-hour format
			// Our test hour is 13 (1 PM), so if we see "1 PM" we know it's 12-hour format
			const hasAmPm = /AM|PM|am|pm/.test(formattedTime);
			
			// Build pattern based on detected format
			if (hasAmPm) {
				// 12-hour format
				return {
					pattern: /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM|am|pm)$/i,
					hourIndex: 1,
					minuteIndex: 2,
					secondIndex: 3,
					ampmIndex: 4
				};
			} else {
				// 24-hour format
				return {
					pattern: /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/,
					hourIndex: 1,
					minuteIndex: 2,
					secondIndex: 3
				};
			}
		},
		
		// Build datetime parsing pattern by combining date and time patterns
		_buildDateTimePattern: function(formattedDateTime, year, month, day, hour, minute, second) {
			// This is a simplified approach - in a real implementation, we'd parse the full datetime format
			// For now, we'll use the detected date pattern + time pattern with a space separator
			const dateInfo = this._buildDatePattern(formattedDateTime.split(' ')[0] || formattedDateTime.split(',')[0], year, month, day);
			const hasAmPm = /AM|PM|am|pm/.test(formattedDateTime);
			
			// Build combined pattern
			let pattern = dateInfo.pattern.source.replace('^', '').replace('$', '');
			pattern += '\\s+';
			pattern += '(\\d{1,2}):(\\d{2})(?::(\\d{2}))?';
			
			let indexMap = {
				yearIndex: dateInfo.yearIndex,
				monthIndex: dateInfo.monthIndex,
				dayIndex: dateInfo.dayIndex,
				hourIndex: 4,
				minuteIndex: 5,
				secondIndex: 6
			};
			
			if (hasAmPm) {
				pattern += '\\s*(AM|PM|am|pm)';
				indexMap.ampmIndex = 7;
			}
			
			pattern = '^' + pattern + '$';
			
			return Object.assign({ pattern: new RegExp(pattern, 'i') }, indexMap);
		},
		
		// Legacy fallback functions (maintain old assumption-based behavior)
		_legacyParseDateTime: function (dateString, strict) {
			const locale = _userLocale;
			// Try to parse common datetime formats based on locale
			if (locale.includes('NZ') || locale.includes('AU')) {
				// DD/MM/YYYY HH:mm:ss format
				return this._tryParseDDMMYYYYDateTime(dateString, strict);
			} else {
				// MM/DD/YYYY HH:mm:ss format (US/default)
				return this._tryParseMMDDYYYYDateTime(dateString, strict);
			}
		},
		
		_legacyParseDate: function (dateString, strict) {
			const locale = _userLocale;
			// Try to parse date-only formats based on locale
			if (locale.includes('NZ') || locale.includes('AU')) {
				// DD/MM/YYYY format
				return this._tryParseDDMMYYYY(dateString, strict);
			} else {
				// MM/DD/YYYY format (US/default)
				return this._tryParseMMDDYYYY(dateString, strict);
			}
		},
		
		_legacyParseTime: function (dateString, strict) {
			const timeDate = this._parseTimeString(dateString);
			if (timeDate) {
				// Create today's date with parsed time
				const today = new Date();
				today.setHours(timeDate.getHours(), timeDate.getMinutes(), timeDate.getSeconds(), 0);
				return today;
			}
			return null;
		},
	};
});